'use client';

import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useIntersection } from '@mantine/hooks';

import { Box, Text, Stack, Group, Flex } from '@mantine/core';

import { useIsMobile } from 'hooks/use-is-mobile';

import { Feature } from '@/../marketing/data/features';
import { Section, SectionProps } from '@/../marketing/components/section/Section';
import { FeaturePreview } from '@/../marketing/components/feature-preview/FeaturePreview';
import { SectionHeader, SectionHeaderProps } from '@/../marketing/components/section/SectionHeader';

import cx from './FeatureShowcase.module.scss';

const FeatureShowcase: FC<
    {
        children: Feature[];
        visualPosition?: 'left' | 'right';

        footer?: React.ReactNode;
        sectionProps?: Partial<SectionProps>;
    } & SectionHeaderProps
> = ({ prefix, title, description, visualPosition = 'right', children, footer, sectionProps }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const activeFeature = children[activeIndex];

    const isMobile = useIsMobile();
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const [progress, setProgress] = useState(0);
    const [isHoverPaused, setIsHoverPaused] = useState(false);

    const { ref: containerRef, entry } = useIntersection({ threshold: 0.2 });
    const isInView = !!entry?.isIntersecting;

    const goToNext = useCallback(() => {
        setActiveIndex((current) => {
            const featuresWithPreview = children.map((f, i) => ({ ...f, originalIndex: i })).filter((f) => f.preview);
            if (!featuresWithPreview.length) return current;

            const currentInFiltered = featuresWithPreview.findIndex((f) => f.originalIndex === current);
            const nextInFiltered = (currentInFiltered + 1) % featuresWithPreview.length;
            return featuresWithPreview[nextInFiltered].originalIndex;
        });
        setProgress(0);
    }, [children]);

    const handleItemClick = (index: number) => {
        setActiveIndex(index);
        setProgress(0);

        if (isMobile) {
            setTimeout(() => {
                const el = itemRefs.current[index];
                if (!el) return;
                const top = el.getBoundingClientRect().top + window.scrollY - 80;
                window.scrollTo({ top, behavior: 'smooth' });
            }, 50);
        }
    };

    useEffect(() => {
        if (isMobile || isHoverPaused || !isInView) return;

        const startTime = Date.now();
        const startProgress = progress;

        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const newProgress = startProgress + (elapsed / 10000) * 100;

            if (newProgress >= 100) {
                goToNext();
            } else {
                setProgress(newProgress);
            }
        }, 50);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [activeIndex, isMobile, isHoverPaused, isInView, progress, goToNext]);

    const FeatureList = (
        <Stack gap={8} className={cx.featureList}>
            {children.map((feature, index) => {
                const isActive = index === activeIndex;
                const hasPreview = !!feature.preview;

                return (
                    <Box
                        key={feature.title}
                        ref={(el) => {
                            itemRefs.current[index] = el;
                        }}
                        className={cx.featureItem}
                        data-active={isActive}
                        data-has-preview={hasPreview}
                        onClick={() => hasPreview && handleItemClick(index)}
                        style={{ cursor: hasPreview ? 'pointer' : 'default' }}
                    >
                        {isActive && (
                            <Box className={cx.progressBarContainer} visibleFrom="md">
                                <Box
                                    className={cx.progressBar}
                                    style={{ width: `${progress}%` }}
                                    data-paused={isHoverPaused}
                                />
                            </Box>
                        )}
                        <Group gap="md" align="flex-start" wrap="nowrap">
                            {feature.icon && (
                                <Flex className={cx.featureIcon} data-active={isActive} align="center" justify="center">
                                    {feature.icon}
                                </Flex>
                            )}
                            <Stack gap={4} className={cx.featureContent}>
                                <Text fw={600} className={cx.featureTitle} data-active={isActive}>
                                    {feature.title}
                                </Text>
                                <Text size="sm" className={cx.featureDescription} data-active={isActive}>
                                    {feature.description}
                                </Text>
                            </Stack>
                        </Group>
                        {isActive && hasPreview && (
                            <Box hiddenFrom="md" mt="sm">
                                <FeaturePreview feature={feature} />
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Stack>
    );

    return (
        <Section {...sectionProps}>
            <Stack gap={60}>
                {title && <SectionHeader prefix={prefix} title={title} description={description} />}

                <Flex
                    ref={containerRef}
                    onMouseEnter={() => setIsHoverPaused(true)}
                    onMouseLeave={() => setIsHoverPaused(false)}
                    gap={{ base: 40, md: 60 }}
                    direction={{ base: 'column', md: 'row' }}
                    align="center"
                    className={cx.container}
                    data-position={visualPosition}
                >
                    {visualPosition === 'left' ? (
                        <>
                            <Box visibleFrom="md">
                                <FeaturePreview key={activeIndex} feature={activeFeature} />
                            </Box>
                            {FeatureList}
                        </>
                    ) : (
                        <>
                            {FeatureList}
                            <Box visibleFrom="md">
                                <FeaturePreview key={activeIndex} feature={activeFeature} />
                            </Box>
                        </>
                    )}
                </Flex>
                {footer}
            </Stack>
        </Section>
    );
};

export { FeatureShowcase };
